import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LandingComponent } from './shared/components/landing/landing.component';
import { AuthGuard } from './guards/auth.guard';
import { AdminGuard } from './guards/is-admin.guard';
import { settingsEntryGuard } from './guards/settings-entry.guard';
import { IsVerifiedGuard } from './guards/is-verified.guard';
import { RulesComponent } from './modules/rules/rules/rules.component';
import { ProfileInfoFilledGuard } from './guards/profile-info-filled.guard';
import { ProfileInfoPendingGuard } from './guards/profile-info-pending.guard';
import { NotFoundComponent } from './modules/not-found/components/not-found.component';
import { ProfileUdatedComponent } from './modules/profile/profile-udated/profile-udated.component';
import { UpdatePasswordComponent } from './modules/auth/components/update-password/update-password.component';
import { MyReservationsComponent } from './modules/my-reservations/my-reservations/my-reservations.component';
import { SuccessComponent } from './shared/components/success/success.component';
import { NotificationsComponent } from './modules/notifications/notifications.component';
import { ReservationViewComponent } from './views/recurring-reservations/components/reservation-view/reservation-view.component';
import { MonthlyJoinLandingComponent } from './shared/components/monthly-join-landing/monthly-join-landing.component';
import { EmailVerificationComponent } from './modules/profile/email-verification/email-verification.component';
import { AchivementsComponent } from './modules/achivements/achivements.component';
import { EventsEnabledGuard } from './guards/events-enabled.guard';
import { featureFlagGuard } from './guards/feature-flag.guard';
import { hasMyDietPlanGuard } from './guards/my-diet-plan.guard';

const routes: Routes = [
  {
    path: '',
    component: RulesComponent,
  },
  {
    path: 'calendar',
    loadChildren: () =>
      import('./modules/calendar/calendar.module').then(
        (m) => m.CalendarModule
      ),
    canActivate: [AuthGuard, IsVerifiedGuard, ProfileInfoFilledGuard, featureFlagGuard],
    data: { themeKey: 'show_calendar', themeDefaultTrue: true },
  },
  {
    path: 'reservation',
    loadChildren: () =>
      import('./modules/reservation/reservation.module').then((m) => {
        return m.ReservationModule;
      }),
    canActivate: [AuthGuard, IsVerifiedGuard, ProfileInfoFilledGuard, featureFlagGuard],
    data: { themeKey: 'show_calendar', themeDefaultTrue: true },
  },
  {
    path: 'settings',
    loadChildren: () =>
      import('./modules/settings/settings.module').then((m) => {
        return m.SettingsModule;
      }),
    // Replaces the previous AdminGuard: admits admins AND non-admins with
    // at least one module permission grant. Each child route's own
    // `modulePermissionGuard` then decides which specific pages they reach.
    canActivate: [settingsEntryGuard],
  },
  {
    path: 'notifications',
    component: NotificationsComponent,
    canActivate: [AuthGuard, IsVerifiedGuard, ProfileInfoFilledGuard],
  },
  {
    path: 'my-reservations',
    component: MyReservationsComponent,
    canActivate: [AuthGuard, IsVerifiedGuard, ProfileInfoFilledGuard, featureFlagGuard],
    data: { themeKey: 'show_calendar', themeDefaultTrue: true },
  },
  {
    path: 'profileSettings',
    loadChildren: () =>
      import('./modules/profile/profile.module').then((m) => m.ProfileModule),
    canActivate: [AuthGuard, IsVerifiedGuard, ProfileInfoFilledGuard],
  },
  {
    path: 'profile',
    loadChildren: () =>
      import('./modules/profile/profile.module').then((m) => m.ProfileModule),
    canActivate: [AuthGuard, ProfileInfoPendingGuard],
  },
  {
    path: 'tournaments',
    loadChildren: () =>
      import('./views/tournaments/tournament.routes').then((m) => m.routes),
    canActivate: [AuthGuard, EventsEnabledGuard],
  },
  {
    path: 'recurring-reservation/:id/:startTime/:courtName',
    component: ReservationViewComponent,
    canActivate: [AdminGuard],
  },
  {
    path: 'recurring-reservation/:id/:startTime/:endTime/:courtName',
    component: ReservationViewComponent,
    canActivate: [AdminGuard],
  },
  {
    path: 'monthly-join-landing',
    component: MonthlyJoinLandingComponent,
    canActivate: [AuthGuard, IsVerifiedGuard, ProfileInfoFilledGuard],
  },
  {
    path: 'pendingVerification',
    component: ProfileUdatedComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'emailVerification',
    component: EmailVerificationComponent,
  },
  {
    path: 'rules',
    component: RulesComponent,
  },
  {
    path: 'club-rules',
    loadComponent: () =>
      import('./modules/club-rules-page/club-rules-page.component').then(
        (m) => m.ClubRulesPageComponent
      ),
  },
  {
    // Terms of use + Privacy policy: public, sign up links here and the
    // native store listings point at <club>/privacy.
    path: 'terms',
    loadComponent: () =>
      import('./views/legal/legal-page.component').then(
        (m) => m.LegalPageComponent
      ),
    data: { doc: 'terms' },
  },
  {
    path: 'privacy',
    loadComponent: () =>
      import('./views/legal/legal-page.component').then(
        (m) => m.LegalPageComponent
      ),
    data: { doc: 'privacy' },
  },
  {
    path: 'success',
    component: SuccessComponent,
    canActivate: [AuthGuard, IsVerifiedGuard, ProfileInfoFilledGuard],
  },
  {
    path: 'updatePassword',
    component: UpdatePasswordComponent,
  },
  {
    path: 'achievements',
    loadComponent: () =>
      import('./modules/achivements/achivements.component').then(
        (m) => m.AchivementsComponent
      ),
    canActivate: [AuthGuard, IsVerifiedGuard, ProfileInfoFilledGuard],
  },
  {
    path: 'discounts',
    loadComponent: () =>
      import('./modules/discounts/discounts.component').then(
        (m) => m.DiscountsComponent
      ),
    canActivate: [AuthGuard, IsVerifiedGuard, ProfileInfoFilledGuard],
  },
  {
    path: 'membership',
    loadComponent: () =>
      import('./views/membership-plans/membership-plans.component').then(
        (m) => m.MembershipPlansComponent
      ),
    canActivate: [AuthGuard, IsVerifiedGuard, ProfileInfoFilledGuard, featureFlagGuard],
    data: { flagKeys: ['stripe_connect', 'subscriptions'] },
  },
  {
    // Member document submission page. AuthGuard ONLY — an unapproved
    // member must be able to reach this (IsVerifiedGuard redirects here),
    // so it must not itself be behind IsVerifiedGuard or it would loop.
    path: 'verification-documents',
    loadComponent: () =>
      import(
        './views/verification-documents-submit/verification-documents-submit.component'
      ).then((m) => m.VerificationDocumentsSubmitComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'my-membership',
    redirectTo: 'membership',
    pathMatch: 'full',
  },
  {
    path: 'leaderboard',
    loadComponent: () =>
      import('./modules/leaderboard/leaderboard.component').then(
        (m) => m.LeaderboardComponent
      ),
    canActivate: [AuthGuard, featureFlagGuard],
    // Both must pass: points must be on AND admin must not have hidden
    // the leaderboard. `themeDefaultTrue` means absent rows / NULL still
    // allow access (backward compat for clubs that pre-date the column).
    data: {
      flagKey: 'points_enabled',
      themeKey: 'show_leaderboard',
      themeDefaultTrue: true,
    },
  },
  {
    path: 'player-preferences',
    loadComponent: () =>
      import('./views/player-preferences/player-preferences.component').then(
        (m) => m.PlayerPreferencesComponent
      ),
    canActivate: [AuthGuard, IsVerifiedGuard, ProfileInfoFilledGuard],
  },
  {
    path: 'my-diet-plan',
    loadComponent: () =>
      import('./modules/my-diet-plan/my-diet-plan.component').then(
        (m) => m.MyDietPlanComponent
      ),
    canActivate: [
      AuthGuard,
      IsVerifiedGuard,
      ProfileInfoFilledGuard,
      featureFlagGuard,
      hasMyDietPlanGuard,
    ],
    data: { flagKey: 'diets' },
  },
  {
    path: 'workouts',
    canActivate: [AuthGuard, IsVerifiedGuard, ProfileInfoFilledGuard, featureFlagGuard],
    data: { flagKey: 'workouts' },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./modules/workouts/workouts.component').then(
            (m) => m.WorkoutsComponent
          ),
      },
      {
        path: 'session/:sessionNumber',
        loadComponent: () =>
          import(
            './modules/workouts/pages/workout-session/workout-session.component'
          ).then((m) => m.WorkoutSessionComponent),
      },
    ],
  },
  {
    path: 'matchmaking/feedback/:reservationId',
    loadComponent: () =>
      import(
        './views/matchmaking-feedback/matchmaking-feedback.component'
      ).then((m) => m.MatchmakingFeedbackComponent),
    canActivate: [AuthGuard, IsVerifiedGuard],
  },
  {
    path: 'public/event/:tournamentId',
    loadComponent: () =>
      import('./views/public-event/public-event.component').then(
        (m) => m.PublicEventComponent
      ),
  },
  {
    path: '404',
    component: NotFoundComponent,
  },
  {
    path: '**',
    redirectTo: '/404',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
